<script setup lang="ts">
import { ProjectModel } from "@xlabmedical/implant-planner";
import {
    MContainerColumn,
    MContainerGrow,
    MContainerRow,
    MContainer,
    MContainerSplit,
    MLoading,
    InputEventBus,
    OrderTypeEnum,
    removeFileExtesion,
    useAssets,
    useUserStore,
    useInput,
    error,
    success,
    TVersionTypes,
} from "@xlabmedical/medic-web-framework";
import { useRouter } from "vue-router";
import { usePatientsStore } from "@/stores/PatientsStore";
import { CopyFileRequestModel, ProjectFileShareModel, ProjectFileType, ProjectPresctiptionTypeRequestModel, ResponseStatusEnum, Share } from "@xlabmedical/medic-api";
import { useToast } from "primevue/usetoast";
import RgShareProjectComponent from "@/components/RgShareProjectComponent.vue";
import { useDialog } from "primevue/usedialog";
import { useConfirm } from "primevue/useconfirm";
import { useI18n } from "vue-i18n";
import { getOrigin } from "@/constants/GlobalConstants";
import { useProductionCenterStore } from "@/stores/ProductionCenterStore";
import { useOrderStore } from "@/stores/OrderStore";
import { computed, ref } from "vue";
import { SegmentationStatus } from "@/models/SegmentationStatus";
import { ExportFileModel } from "@/models/ExportFileModel";
import RgProjectImplantList from "@/components/ProjectItem/RgProjectImplantList.vue";
import { useWindowSize } from "@vueuse/core";
import Dialog from "primevue/dialog";
import RgProjectItemInfo from "./RgProjectItemInfo.vue";
import RgProjectItemHeader from "./RgProjectItemHeader.vue";
import RgProjectItemActions from "./RgProjectItemActions.vue";

const { t } = useI18n();

const toast = useToast();
const dialog = useDialog();
const input = useInput();

const api = useServerApi();

const router = useRouter();
const assets = useAssets();
const auth = useAuth();
const license = auth.getSessionLicence();
const user = ref(auth.getUser());
const hasOpenAllProjectsParam = ref(auth.hasOpenAllProjectsParam(user.value));

const enableAI = ref(false);
if (license) enableAI.value = !(license.versionType & TVersionTypes.START);

interface Props {
    project: ProjectModel;
    exportToAlfred?: boolean;
    revisionIndex?: number;
    username?: string;
    sharedWith?: Share[];
}

const props = withDefaults(defineProps<Props>(), {
    exportToAlfred: false,
    revisionIndex: 0,
});

const multipleSelected = defineModel<boolean>("multipleSelected", { default: false });

const emits = defineEmits(["reload-patient-store", "segmentationTriggered", "exportToAlfred", "sendToWeblinkClicked", "exportClicked", "deleteClicked", "revisionApprovalClicked"]);

const confirm = useConfirm();

const userStore = useUserStore();
const orderStore = useOrderStore();

const patientStore = usePatientsStore();
const { patients } = storeToRefs(patientStore);

const productionCenterStore = useProductionCenterStore();
const { productionCenterList } = storeToRefs(productionCenterStore);

const preparingOrder = ref(false);
const preparingOrderText = ref(t("weblink.uploadingAttachmentsToOrder"));

const showImplants = ref(false);

const imageUrl = computed(() => {
    if (props.project.imageLoading || !props.project.imageBuffer) {
        return "";
    }
    return `data:image/png;base64,${props.project.imageBuffer}`;
});

const { width, height } = useWindowSize();

const isLargeScreen = computed(() => width.value > 2200);
const isTabletOrLess = computed(() => width.value < 1200);

const projectInfoMaxHeight = computed(() => (height.value < 1200 ? "14rem" : "16rem"));

function getProjectName(name: string) {
    return name.replace(".zip", "");
}

const onOpenClick = async () => {
    if (disableProject.value) {
        await onOpenBlockedClick();
        return;
    }

    const isReadOnly = !getAllowsUserEditingInfo.value;
    const cleanQuery: Record<string, string> = {};

    if (isReadOnly && hasWeblinkOrder.value) {
        cleanQuery.readOnly = "true";

        const approved = await onOpenApprovalClick();
        if (!approved) {
            return;
        }
    }

    await router.push({
        name: "ImplantPlannerProjectLoaderView",
        params: {
            projectHash: props.project.projectUUID,
            fileHash: props.project.uuid,
        },
        query: cleanQuery,
    });
};

const onOpenBlockedClick = (): Promise<void> =>
    new Promise((resolve) => {
        const resolveOnce = (() => {
            let done = false;
            return () => {
                if (done) return;
                done = true;
                resolve();
            };
        })();

        confirm.require({
            group: "approveOpenProjectConfirmation",
            header: t("implant-planner.warning"),
            message: t("web-rg.thisProjectBelongsToADifferentVendorPleaseContactSupportToRequestAccess"),
            acceptIcon: "pi pi-check",
            acceptLabel: t("shared.ok"),
            acceptClass: "p-button-sm",
            defaultFocus: "accept",
            rejectProps: { style: "display: none" },
            onHide: resolveOnce,
            accept: resolveOnce,
        });
    });

const onOpenApprovalClick = async (): Promise<boolean> => {
    return new Promise((resolve) => {
        confirm.require({
            group: "approveOpenProjectConfirmation",
            header: t("web-rg.projectIsReadOnly"),
            message: t("web-rg.youCantEditThisProjectWhileAnOrderIsActiveAskYourOperatorToAllowEditing"),
            acceptIcon: "pi pi-check",
            rejectIcon: "pi pi-times",
            rejectLabel: t("shared.cancel"),
            acceptLabel: t("web-rg.openReadOnlyProject"),
            acceptClass: "p-button-sm",
            rejectClass: "p-button-outlined p-button-sm",
            accept: async () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
        });
    });
};

const onExportClick = () => {
    const model = {
        fileUuid: props.project.uuid,
        downloadFilename: props.project.downloadFilename,
    } as ExportFileModel;

    emits("exportClicked", model);
};

const onCreateOrderClick = async () => {
    const patient = patients.value.find((p) => p.patientId === props.project.patientId);

    const patientName = `${patient?.patientName}${props.project.projectDisplayName.replace(".zip", "")}` || t("web-rg.noPatientNameFound");

    preparingOrder.value = true;

    const requestData = {
        notes: "",
        dateOfBirth: new Date(),
        firstName: patient?.firstName,
        lastName: patient?.lastName,
        extra: {
            patientName: patient?.firstName,
            lastName: patient?.lastName,
            patientID: patientName,
            modality: "",
            doctorID: "",
            study_instance_UID: patientName,
        },
    };

    const sourceProjectName = patient?.extra.study_instance_UID || "";
    const metadata = await patientStore.getProjectFileMetaDataById(props.project.projectFileId);

    const projectPrescriptionModel = {
        projectName: patientName,
        originProjectName: sourceProjectName,
        originProjectFileName: props.project.projectFilename,
        patientData: JSON.stringify(requestData),
    } as ProjectPresctiptionTypeRequestModel;

    const projectResponse = await patientStore.addProjectPrescriptionType(projectPrescriptionModel);

    if (projectResponse.status === ResponseStatusEnum.OK) {
        // copy project files to weblink project
        const filenames = [
            { suffix: "", targetType: ProjectFileType.ProjectAttachment },
            { suffix: ".mobile.json", targetType: ProjectFileType.ProjectAttachmentPreview },
        ];

        let copiedFileId = 0;
        for (const { suffix, targetType } of filenames) {
            const fileName = `${props.project.projectFilename}${suffix}`;
            const copyModel: CopyFileRequestModel = {
                sourceProjectName,
                sourceFileName: fileName,
                targetProjectName: patientName,
                targetFileName: fileName,
                targetMetadata: JSON.stringify(metadata),
                targetType,
                force: true,
            };

            try {
                const id = await patientStore.copyFileBetweenProjects(copyModel);

                if (id) {
                    if (targetType === ProjectFileType.ProjectAttachment) {
                        copiedFileId = id;
                    }
                }
            } catch (error) {
                toast.add({
                    severity: "error",
                    summary: t("shared.error"),
                    detail: `${t("shared.errorWhileCopyingFileBetweenProjects")}: ${error}`,
                    life: 3000,
                });

                console.error(t("shared.errorWhileCopyingFileBetweenProjects"), error);
            }
        }

        const token = await userStore.getUserSessionToken();

        const url = `${getOrigin()}/weblink/order.html?token=${token}&fileId=${copiedFileId}&orderType=${OrderTypeEnum.ORDER_NO_ATTACHMENT}&projectId=${projectResponse.data.id}`;

        if (copiedFileId > 0) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    } else {
        toast.add({
            severity: "error",
            summary: t("shared.error"),
            detail: `${t("web-rg.errorWhileCreatingProjectWithPrescriptionType")}: ${projectResponse.message}`,
            life: 3000,
        });
    }

    preparingOrder.value = false;
};

const onShareClick = async () => {
    const projectName = props.project.weblinkOrderData?.weblinkProjectName ? props.project.weblinkOrderData.weblinkProjectName : props.project.patientName;
    const fileName = props.project.projectFilename;

    dialog.open(RgShareProjectComponent, {
        props: {
            header: t("shared.shareProjectFile"),
            style: { width: "50rem" },
            modal: true,
            dismissableMask: true,
        },
        data: {
            isFileShare: true,
        },
        emits: {
            onShareProjectFile: async (message: string, username: string, editable: boolean, anonymize: boolean, analogHoles: boolean) => {
                const data = {
                    projectName: projectName,
                    withUsername: username,
                    message: message,
                    editable: editable,
                    anonymize: anonymize,
                    analogHoles: analogHoles,
                    fileName: fileName,
                } as ProjectFileShareModel;

                const mobileData = {
                    ...data,
                    fileName: `${fileName}.mobile.json`,
                };

                const shareResponse = await patientStore.shareProjectFile(data);
                const shareResponseMobile = await patientStore.shareProjectFile(mobileData);

                if (!shareResponse.success) {
                    error(`${t("web-rg.errorWhileSharingProjectFile")}: ${shareResponse.response.data.message}`, t("shared.error"));
                }

                if (!shareResponseMobile.success) {
                    error(`${t("web-rg.errorWhileSharingProjectFile")}: ${shareResponseMobile.response.data.message}`, t("shared.error"));
                }

                const bothSuccess = shareResponse.success && shareResponseMobile.success;

                if (bothSuccess) {
                    success(t("web-rg.successfullySharedProjectFile"), t("shared.success"));
                }
            },
        },
    });
};

const onDeleteClick = async () => {
    const _patientName = props.project.weblinkOrderData?.weblinkProjectName ?? props.project.patientName;

    confirm.require({
        group: "deleteProjectItem",
        message: t("web-rg.deleteProjectConfirm", { projectName: props.project.projectFilename }).replace(/\\n/g, "\n"),
        header: t("web-rg.deleteProject"),
        icon: "pi pi-exclamation-triangle",
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        modal: true,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true,
        accept: async () => {
            if (props.project.weblinkOrderData?.weblinkProjectName) {
                await api.deleteProjectFile(props.project.weblinkOrderData?.weblinkProjectName, props.project.projectFilename);
            } else {
                await api.deleteProjectFile(props.project.patientName, props.project.projectFilename);
                await api.deleteProjectFile(_patientName, props.project.projectFilename);
                if (props.project.mobileFilename) {
                    await api.deleteProjectFile(_patientName, props.project.mobileFilename);
                }
                toast.add({
                    severity: "info",
                    summary: t("web-rg.projectDeleted"),
                    detail: `${t("web-rg.youHaveDeletedProject")}: ${removeFileExtesion(props.project.projectFilename)}`,
                    life: 3000,
                });
                confirm.close();
                emits("reload-patient-store");
            }
        },
        reject: () => {
            confirm.close();
        },
    });
};

const onUpdateOrder = () => {
    const pcUrl = productionCenterList.value.find((p) => p.id === props.project.weblinkOrderData?.productionCenterId)?.url;

    const name = encodeURIComponent(props.project.weblinkOrderData?.weblinkProjectName || "");
    const url = `${getOrigin()}/weblink/${pcUrl}/patients/${name}`;

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
};

async function approveRevision() {
    const productionCenterId = props.project.weblinkOrderData?.productionCenterId;
    const prescriptionId = props.project.weblinkOrderData?.weblinkOrderId;
    const revisionPfId = props.project.weblinkOrderData?.revisionPfId || 0;

    if (revisionPfId === 0) {
        toast.add({
            severity: "error",
            summary: t("shared.error"),
            detail: `${t("shared.revisionFileNotFound")}!`,
            life: 3000,
        });
    }

    const response = await orderStore.acceptPrescriptionRevision(productionCenterId || 0, prescriptionId || 0, revisionPfId);

    if (response === ResponseStatusEnum.OK) {
        toast.add({
            severity: "success",
            summary: t("shared.success"),
            detail: t("shared.approvedSuccessfully"),
            life: 3000,
        });
    } else {
        toast.add({
            severity: "error",
            summary: t("shared.error"),
            detail: `${t("shared.errorWhileApprovingRevision")}: ${response}`,
            life: 3000,
        });
    }
}

const onRevisionApprovalClick = async () => {
    confirm.require({
        group: "approveRgConfirmation",
        header: t("shared.approveRevision"),
        message: t("weblink.areYouSureYouWantToAcceptThisRevisionForYourProject"),
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        rejectLabel: t("shared.cancel"),
        acceptLabel: t("shared.approve"),
        acceptClass: "p-button-sm",
        rejectClass: "p-button-outlined p-button-sm",
        accept: async () => {
            await approveRevision();
        },
    });
};

const hasWeblinkOrder = computed(() => {
    return (props.project.weblinkOrderData?.weblinkOrderId || 0) > 0;
});

const showRevisionBtn = computed(() => {
    return hasWeblinkOrder.value && getAwaitUserConfirmationInfo.value && props.revisionIndex === 0;
});

const showUpdateBtn = computed(() => {
    if (!hasWeblinkOrder.value) {
        return false;
    }

    return getAllowsUserEditingInfo.value;
});

const hideProjectItemButtonLabels = computed(() => {
    return hasWeblinkOrder.value ? width.value < 1500 : width.value < 1450;
});

const shareNames = computed(() => {
    const allShares: Share[] = [...(props.project.shares ?? []), ...(props.sharedWith ?? [])];
    const names = new Set<string>();

    allShares.forEach((share) => {
        const name = share.withUser?.name;
        if (name) {
            names.add(name);
        }
    });

    return Array.from(names);
});

const showSharesIcon = computed(() => {
    if (!isMine.value) {
        return true;
    }

    return shareNames.value.length > 0 && !hasWeblinkOrder.value;
});

const onOpenWeblinkOrderClick = async () => {
    const pcUrl = productionCenterList.value.find((p) => p.id === props.project.weblinkOrderData?.productionCenterId)?.url;

    const routeUrl = router.resolve({
        name: "OpenWeblinkOrder",
        params: {
            projectName: props.project.weblinkOrderData?.weblinkProjectName,
            productionCenterName: pcUrl,
        },
    }).href;

    window.open(routeUrl, "_blank", "noopener");
};

const getAllowsUserEditingInfo = computed(() => {
    const allowsUserEditing = props.project.weblinkOrderData?.allowsUserEditing;

    if (allowsUserEditing !== undefined) {
        return allowsUserEditing;
    }
    return false;
});

const getAwaitUserConfirmationInfo = computed(() => {
    const awaitUserConfirmation = props.project.weblinkOrderData?.awaitUserConfirmation;

    if (awaitUserConfirmation !== undefined) {
        return awaitUserConfirmation;
    }
    return false;
});

const showSegmentationButton = computed(() => {
    return (
        (props.project.segmentationStatus === SegmentationStatus.NOT_STARTED ||
            props.project.segmentationStatus === SegmentationStatus.FAILED ||
            !props.project.segmentationInvokedByUser) &&
        enableAI.value
    );
});

const inSegmentationProcess = computed(() => {
    return props.project.segmentationStatus === SegmentationStatus.IN_PROCESS && props.project.segmentationInvokedByUser;
});

const onSegmentClick = () => {
    emits("segmentationTriggered", props.project.id);
};

const sharedWithText = computed(() => {
    const sharedBy = `${t("web-rg.sharedBy")} ${props.project?.uploadedBy?.name}`;

    if (!isMine.value && shareNames.value.length > 0) {
        return `${sharedBy} | ${t("web-rg.sharedWith")} ${shareNames.value.join(", ")}`;
    }

    if (shareNames.value.length > 0) {
        return `${t("web-rg.sharedWith")} ${shareNames.value.join(", ")}`;
    }
    if (!isMine.value) {
        return sharedBy;
    }

    return t("web-rg.noShares");
});

type ExtraMenuTemplateItem = {
    label: string;
    icon?: string;
    command?: () => void;
    disabled?: boolean;
    checked?: boolean;
};

const editableAsProInProgress = ref(false);
const editableAsProOverride = ref<boolean | null>(null);

const getCurrentProjectFile = () => {
    return (
        props.project.projectFileList?.find((file) => file.id === props.project.projectFileId) ??
        props.project.projectFileList?.find((file) => file.name === props.project.projectFilename)
    );
};

const isProjectEditableAsPro = computed(() => {
    if (editableAsProOverride.value !== null) {
        return editableAsProOverride.value;
    }

    return getCurrentProjectFile()?.editableAsPro ?? false;
});

const onToggleProjectEditableAsPro = async () => {
    if (editableAsProInProgress.value) {
        return;
    }

    const editable = !isProjectEditableAsPro.value;
    editableAsProInProgress.value = true;

    try {
        await patientStore.setProjectFileEditableAsPro({
            projectName: props.project.patientName,
            fileName: props.project.projectFilename,
            editable,
        });
        editableAsProOverride.value = editable;
    } catch (err) {
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as { message?: string })?.message ?? t("shared.error");
        error(errorMessage, t("shared.error"));
    } finally {
        editableAsProInProgress.value = false;
    }
};

const transferOwnershipInProgress = ref(false);
let validateTransferOwnershipInput = false;
const transferOwnershipRequiredMessage = `${t("web-rg.withUserEmail")} ${t("shared.isRequired")}`;

InputEventBus.on("validate-input", (event: { _name: string; target: { value: string } }) => {
    if (!validateTransferOwnershipInput) {
        return;
    }

    const isValid = event.target.value.trim() !== "";
    InputEventBus.emit("validate-result", {
        valid: isValid,
        message: isValid ? "" : transferOwnershipRequiredMessage,
    });
});

const onTransferOwnershipConfirm = async () => {
    if (transferOwnershipInProgress.value) {
        return;
    }

    let response: string | null;
    validateTransferOwnershipInput = true;

    try {
        response = await input.input({
            message: t("web-rg.enterTheEmailOfTheUserYouWishToTransferTheOwnershipTo"),
            acceptLabel: t("shared.ok"),
            rejectLabel: t("shared.cancel"),
            title: t("web-rg.transferOwnership"),
            invalid: true,
            invalidMsg: transferOwnershipRequiredMessage,
        });
    } finally {
        validateTransferOwnershipInput = false;
    }

    if (response == undefined) {
        return;
    }

    transferOwnershipInProgress.value = true;

    try {
        await patientStore.setProjectOwner({
            projectName: props.project.patientName,
            owner: response.trim(),
        });

        success(t("web-rg.projectOwnershipTransferredSuccessfully"), t("shared.success"));
        emits("reload-patient-store");
    } catch (err) {
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as { message?: string })?.message ?? t("shared.error");
        error(t("web-rg.errorWhileTransferringOwnership", { errorMessage }), t("shared.error"));
    } finally {
        transferOwnershipInProgress.value = false;
    }
};

const isMine = computed(() => props.project?.uploadedBy?.username === user.value?.username);

const disableProject = computed(() => {
    const isCurrentVendorProject = patientStore.isProjectFromCurrentVendor(props.project.versionCompany);

    return !isCurrentVendorProject && !hasOpenAllProjectsParam.value;
});

const isProjectActionDisabled = computed(() => {
    return props.project.imageLoading || disableProject.value;
});

const itemsExtra = computed(() => {
    const items: ExtraMenuTemplateItem[] = [
        {
            label: t("shared.export"),
            icon: assets.getAssetUrl("/icons/rg_upgrade_rotated.svg"),
            command: onExportClick,
        },
    ];

    if (isMine.value) {
        items.push({
            label: t("web-rg.transferOwnership"),
            icon: "pi pi-user-edit",
            command: onTransferOwnershipConfirm,
        });
    }

    if (auth.hasEditableAsPro()) {
        items.push({
            label: t("web-rg.enableProEditing"),
            command: onToggleProjectEditableAsPro,
            checked: isProjectEditableAsPro.value,
            disabled: editableAsProInProgress.value,
        });
    }

    items.push({
        label: t("web-rg.segment"),
        icon: assets.getAssetUrl("/icons/toolbar_icons/ik_xray_28.svg"),
        command: onSegmentClick,
        disabled: !showSegmentationButton.value,
    });

    return [{ items }];
});

const toggleProjectInfo = () => {
    showImplants.value = !showImplants.value;
};
</script>

<template>
    <Dialog v-model:visible="preparingOrder" class="preparing-dialog" :closable="false" :draggable="false" modal :header="$t('web-rg.preparingOrder')">
        <m-loading :strokeWidth="5">
            <span>{{ preparingOrderText }}</span>
        </m-loading>
    </Dialog>
    <MContainerRow v-if="isTabletOrLess" class="rg-project-item-poc h-full">
        <MContainerRow class="items-center" style="max-height: 50px">
            <RgProjectItemHeader
                :projectName="getProjectName(project.projectFilename)"
                :hasWeblinkOrder="hasWeblinkOrder"
                :isLargeScreen="isLargeScreen"
                :showSharesIcon="showSharesIcon"
                :sharedWithText="sharedWithText"
                @toggle-project-info="toggleProjectInfo"
                @open-weblink-order="onOpenWeblinkOrderClick" />
        </MContainerRow>
        <MContainerRow v-if="isTabletOrLess">
            <MContainer :class="[!isLargeScreen ? 'h-full img-card-size-poc-ipad' : '']" style="width: 40% !important">
                <div v-if="!props.project.imageLoading" class="relative w-full h-full">
                    <img
                        :src="imageUrl"
                        :class="[
                            'w-full h-full object-contain left-bottom-radius img-card-size-poc-ipad',
                            multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
                        ]"
                        style="object-fit: cover"
                        alt=""
                        draggable="false"
                        @click="onOpenClick" />
                </div>
                <Skeleton v-else class="skeleton-card-size-poc-ipad left-radius" />
            </MContainer>
            <MContainerSplit>
                <MContainerColumn class="w-full pt-2" :class="[multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : '']">
                    <MContainerRow class="h-full overflow-y-auto" :style="{ maxHeight: projectInfoMaxHeight }">
                        <RgProjectItemInfo v-if="!showImplants" :project="props.project" :hasWeblinkOrder="hasWeblinkOrder" />
                        <RgProjectImplantList v-else :implant-list="project.implantList" :isPopover="true" />
                    </MContainerRow>
                    <Divider />
                    <RgProjectItemActions
                        style="max-height: 3rem !important; margin-top: -1rem !important"
                        :isLargeScreen="isLargeScreen"
                        :allowsUserEditing="getAllowsUserEditingInfo"
                        :showUpdateBtn="showUpdateBtn"
                        :showRevisionBtn="showRevisionBtn"
                        :hideProjectItemButtonLabels="hideProjectItemButtonLabels"
                        :openButtonDataCy="`openProjectButton-${getProjectName(project.projectFilename)}`"
                        :openButtonDisabled="props.project.imageLoading"
                        :inSegmentationProcess="inSegmentationProcess"
                        :hasWeblinkOrder="hasWeblinkOrder"
                        :isProjectActionDisabled="isProjectActionDisabled"
                        :itemsExtra="itemsExtra"
                        @open-click="onOpenClick"
                        @delete-click="onDeleteClick"
                        @share-click="onShareClick"
                        @create-order-click="onCreateOrderClick"
                        @update-order-click="onUpdateOrder"
                        @revision-approval-click="onRevisionApprovalClick" />
                </MContainerColumn>
            </MContainerSplit>
        </MContainerRow>
    </MContainerRow>
    <MContainerRow v-else class="rg-project-item-poc">
        <MContainerSplit :class="[!isLargeScreen ? 'h-full' : '']">
            <div v-if="!props.project.imageLoading" class="relative w-full h-full">
                <img
                    :src="imageUrl"
                    :class="[
                        'w-full h-full object-contain left-radius img-card-size-poc',
                        multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
                    ]"
                    style="object-fit: cover"
                    alt=""
                    draggable="false"
                    @click="onOpenClick" />
            </div>
            <Skeleton v-else class="skeleton-card-size-poc left-radius" />
        </MContainerSplit>
        <MContainerSplit>
            <MContainerColumn class="w-full pt-2 gap-6" :class="[multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : '']">
                <RgProjectItemHeader
                    :projectName="getProjectName(project.projectFilename)"
                    :hasWeblinkOrder="hasWeblinkOrder"
                    :isLargeScreen="isLargeScreen"
                    :showSharesIcon="showSharesIcon"
                    :sharedWithText="sharedWithText"
                    @toggle-project-info="toggleProjectInfo"
                    @open-weblink-order="onOpenWeblinkOrderClick" />
                <MContainerRow class="h-full overflow-y-auto" :style="{ maxHeight: projectInfoMaxHeight }">
                    <RgProjectItemInfo v-if="!showImplants" :project="props.project" :hasWeblinkOrder="hasWeblinkOrder" />
                    <RgProjectImplantList v-else :implant-list="project.implantList" :isPopover="true" />
                </MContainerRow>
                <MContainerGrow class="-mt-2 rg-border-bottom" />
                <RgProjectItemActions
                    :isLargeScreen="isLargeScreen"
                    :allowsUserEditing="getAllowsUserEditingInfo"
                    :showUpdateBtn="showUpdateBtn"
                    :showRevisionBtn="showRevisionBtn"
                    :hideProjectItemButtonLabels="hideProjectItemButtonLabels"
                    :openButtonDataCy="`openProjectButton-${getProjectName(project.projectFilename)}`"
                    :openButtonDisabled="props.project.imageLoading"
                    :inSegmentationProcess="inSegmentationProcess"
                    :hasWeblinkOrder="hasWeblinkOrder"
                    :isProjectActionDisabled="isProjectActionDisabled"
                    :itemsExtra="itemsExtra"
                    @open-click="onOpenClick"
                    @delete-click="onDeleteClick"
                    @share-click="onShareClick"
                    @create-order-click="onCreateOrderClick"
                    @update-order-click="onUpdateOrder"
                    @revision-approval-click="onRevisionApprovalClick" />
            </MContainerColumn>
        </MContainerSplit>
    </MContainerRow>
</template>

<style>
.rg-shared-with-tooltip-project.p-tooltip {
    max-width: min(36rem, calc(100vw - 2rem));
    z-index: 1100 !important;
}

.rg-shared-with-tooltip-project .p-tooltip-text {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

@media (max-width: 768px) {
    .rg-shared-with-tooltip-project.p-tooltip {
        max-width: 85vw;
    }
}
</style>

<style scoped>
.preparing-dialog {
    width: 25vw;
}

:deep(.m-u-button > i) {
    padding-top: 1rem !important;
}

:deep(.m-u-button > i + span) {
    margin-top: 0.45rem !important;
}

@media (max-width: 2000px) {
    .preparing-dialog {
        width: 35vw;
    }
}
</style>
